import React, { useState, useEffect, useCallback } from 'react';
import { Share2, Copy, Check, Users, Gift, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralPage = ({ onBack, user }) => {
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [credits, setCredits] = useState([]);
  const [stats, setStats] = useState({});
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [codeRes, refRes] = await Promise.all([
        fetch(`${API_URL}/api/referrals/my-code/${user.id}`),
        fetch(`${API_URL}/api/referrals/my-referrals/${user.id}`)
      ]);
      const codeData = await codeRes.json();
      const refData = await refRes.json();

      if (codeData.success) {
        setReferralCode(codeData.referral_code);
        setReferralLink(codeData.referral_link);
      }
      if (refData.success) {
        setReferrals(refData.referrals || []);
        setCredits(refData.active_credits || []);
        setStats(refData.stats || {});
      }
    } catch {}
  }, [user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = referralCode;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = referralLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Yachangu!',
          text: `Use my referral code ${referralCode} to get a discount on your rides!`,
          url: referralLink
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto" data-testid="referral-page">
      <div className="p-4 border-b dark:border-gray-700">
        <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2" data-testid="referral-back-btn">
          <span>&larr;</span>
          <span>Refer & Earn</span>
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Hero */}
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <Gift className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Refer Friends, Earn Rewards</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
            Share your code. When they ride, you earn discounts{user?.role === 'driver' ? ' & cash bonuses' : ''}!
          </p>
        </div>

        {/* Referral Code Card */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-5 border border-green-200 dark:border-green-800">
          <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2 uppercase tracking-wide">Your Referral Code</p>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-mono font-bold text-gray-900 dark:text-white tracking-wider flex-1" data-testid="referral-code-display">{referralCode || '...'}</span>
            <button
              onClick={copyCode}
              data-testid="copy-referral-code-btn"
              className="flex items-center gap-1 px-3 py-2 bg-white dark:bg-gray-800 rounded-lg border border-green-300 dark:border-green-700 text-sm font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/40 transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="flex gap-3">
          <button
            onClick={shareLink}
            data-testid="share-referral-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Share Link
          </button>
          <button
            onClick={copyLink}
            data-testid="copy-referral-link-btn"
            className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl font-medium text-gray-700 dark:text-gray-300 transition-colors"
          >
            {copiedLink ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            {copiedLink ? 'Copied!' : 'Link'}
          </button>
        </div>

        {/* Info for existing users */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">How it works</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Share your code with friends who haven't joined Yachangu yet. They enter your code when registering, and once they complete their first trip, you earn your reward!
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700 text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.total_referred || 0}</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Referred</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700 text-center">
            <p className="text-lg font-bold text-green-600">{stats.credits_available || 0}</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Credits</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700 text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.credits_used || 0}</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Used</p>
          </div>
        </div>

        {/* Active Credits */}
        {credits.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Gift className="w-4 h-4 text-green-600" />
              Your Rewards
            </h3>
            <div className="space-y-2">
              {credits.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {c.discount_percent > 0 ? `${c.discount_percent}% off` : `MWK ${c.bonus_amount}`}
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">{c.description}</p>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-gray-400">
                    <Clock className="w-3 h-3" />
                    {new Date(c.expires_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral List */}
        {referrals.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-green-600" />
              Your Referrals
            </h3>
            <div className="space-y-2">
              {referrals.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {r.referee_role === 'driver' ? 'Driver' : 'Rider'} referral
                    </p>
                    <p className="text-[10px] text-gray-500">{r.trips_completed}/{r.required_trips} trips completed</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    r.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {r.status === 'completed' ? 'Earned' : 'In Progress'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
